import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Typography,
  Card,
  Table,
  Button,
  Modal,
  Form,
  DatePicker,
  InputNumber,
  message,
} from "antd";
import { RocketOutlined, PlusOutlined } from "@ant-design/icons";
// adjust this import path:
import api from "../../api";

const { Title, Text } = Typography;

interface MonthlySummary {
  id: number;
  month: string;      // "2026-02-01"
  revenue: number;
  expenses: number;
  pnl: number;
}

const OverviewPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [data, setData] = useState<MonthlySummary[]>([]);
  const [loading, setLoading] = useState(false);

  const columns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      render: (value: string) => value.slice(0, 7), // YYYY-MM
    },
    {
      title: "Revenue (₩)",
      dataIndex: "revenue",
      key: "revenue",
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: "Expenses (₩)",
      dataIndex: "expenses",
      key: "expenses",
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: "P&L (₩)",
      dataIndex: "pnl",
      key: "pnl",
      render: (value: number) => value.toLocaleString(),
    },
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/ul/monthly-summaries/");
      const list: any[] = res.data;
      setData(
        list.map((item) => ({
          id: item.id,
          month: item.month,
          revenue: Number(item.revenue),
          expenses: Number(item.expenses),
          pnl: Number(item.pnl),
        }))
      );
    } catch (err) {
      console.error(err);
      message.error("Failed to load monthly summaries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    const monthPickerValue = values.month; // Dayjs or Moment
    if (!monthPickerValue) return;

    const monthStr = monthPickerValue.startOf("month").format("YYYY-MM-01");

    const payload = {
      month: monthStr,
      revenue: values.revenue ?? 0,
      expenses: values.expenses ?? 0,
    };

    try {
      const res = await api.post("/ul/monthly-summaries/", payload);
      message.success("Monthly summary saved.");
      // Option 1: reload list from server
      await loadData();
      // Option 2: optimistic add using res.data
    } catch (err: any) {
      console.error(err);
      message.error("Failed to save monthly summary.");
    } finally {
      handleCloseModal();
    }
  };

  return (
    <div style={{ padding: "40px", background: "#f0f2f5", minHeight: "100vh" }}>
      {/* Title Card */}
      <Card
        bordered={false}
        style={{
          maxWidth: 900,
          margin: "0 auto 40px auto",
          textAlign: "center",
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <RocketOutlined style={{ fontSize: 48, color: "#1890ff", marginBottom: 16 }} />

        <Title level={2} style={{ marginBottom: 8 }}>
          Ultra Lounge Overview Page
        </Title>

        <Text type="secondary">
          Monthly revenue, expenses, and P&amp;L overview (data input manually each month)
        </Text>
      </Card>

      {/* Monthly Summary Table Card */}
      <Row justify="center">
        <Col xs={24} sm={22} md={20} lg={16}>
          <Card
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Title level={4} style={{ marginBottom: 0 }}>
                Monthly Financial Summary
              </Title>

              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal}>
                Add Monthly Summary
              </Button>
            </div>

            <Text type="secondary">
              This table tracks monthly revenue, expenses, and net P&amp;L for the Ultra Lounge.
              Data is entered manually each month.
            </Text>

            <div style={{ marginTop: 24 }}>
              <Table<MonthlySummary>
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                pagination={false}
                locale={{
                  emptyText: "No data yet — monthly inputs will appear here.",
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Add Monthly Summary Modal */}
      <Modal
        title="Add Monthly Summary"
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={() => form.submit()}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Month"
            name="month"
            rules={[{ required: true, message: "Please select a month" }]}
          >
            <DatePicker picker="month" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Revenue (₩)"
            name="revenue"
            rules={[{ required: true, message: "Please enter revenue" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item
            label="Expenses (₩)"
            name="expenses"
            rules={[{ required: true, message: "Please enter expenses" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OverviewPage;
